/**
 * engine-params.js — the parameter catalogue for every DSonic voice.
 *
 * Pure data: the nine primary parameters the patch matrix puts on screen, the
 * full per-section set behind the deep-edit panel, and the two naming tables.
 * `../engine-data.js` is the module the Studio actually imports; this file
 * exists to keep ~900 lines of tables out of it.
 */

export const ENGINE_PRIMARY_PARAMS = {
  analog_kick: [
    { name: 'bodyPitch', label: 'Pitch', min: 30, max: 120, default: 55, unit: 'Hz', decimals: 0 },
    { name: 'bodyDecay', label: 'Body', min: 0.05, max: 1, default: 0.3, unit: 's' },
    { name: 'pitchEnvAmt', label: 'PEnv', min: 0, max: 48, default: 24, unit: 'st', decimals: 0 },
    { name: 'pitchEnvDecay', label: 'PDecay', min: 0.01, max: 0.5, default: 0.08, unit: 's' },
    { name: 'clickLevel', label: 'Click', min: 0, max: 1, default: 0.3 },
    { name: 'noiseLevel', label: 'Noise', min: 0, max: 1, default: 0 },
    { name: 'driveAmt', label: 'Drive', min: 0, max: 1, default: 0.2 },
    { name: 'lowShelfGain', label: 'LowBst', min: -6, max: 12, default: 3, unit: 'dB', decimals: 1 },
    { name: 'decay', label: 'Decay', min: 0.05, max: 1, default: 0.3, unit: 's' },
  ],
  membrane: [
    { name: 'pitch1', label: 'Pitch1', min: 80, max: 400, default: 180, unit: 'Hz', decimals: 0 },
    { name: 'pitch2', label: 'Pitch2', min: 80, max: 400, default: 220, unit: 'Hz', decimals: 0 },
    { name: 'coupling12', label: 'Couple', min: 0, max: 1, default: 0.3 },
    { name: 'wireLevel', label: 'Wire', min: 0, max: 1, default: 0.5 },
    { name: 'snareWireTension', label: 'WireT', min: 0, max: 1, default: 0.5 },
    { name: 'strikeHardness', label: 'Hard', min: 0, max: 1, default: 0.5 },
    { name: 'toneTilt', label: 'Tilt', min: -1, max: 1, default: 0 },
    { name: 'driveAmt', label: 'Dist', min: 0, max: 1, default: 0 },
    { name: 'decay', label: 'Decay', min: 0.05, max: 0.5, default: 0.15, unit: 's' },
  ],
  hybrid_snare: [
    { name: 'bodyPitch', label: 'Body', min: 80, max: 400, default: 180, unit: 'Hz', decimals: 0 },
    { name: 'wireLevel', label: 'Wire', min: 0, max: 1, default: 0.4 },
    { name: 'wireTension', label: 'WireT', min: 0, max: 1, default: 0.5 },
    { name: 'noiseLevel', label: 'Noise', min: 0, max: 1, default: 0.6 },
    { name: 'noiseNarrowAmt', label: 'Narrow', min: 0, max: 1, default: 0.5 },
    { name: 'filterEnvAmt', label: 'FiltEnv', min: 0, max: 1, default: 0.3 },
    { name: 'driveAmt', label: 'Clip', min: 0, max: 1, default: 0.2 },
    { name: 'hpfCutoff', label: 'HPF', min: 20, max: 500, default: 80, unit: 'Hz', decimals: 0 },
    { name: 'bodyDecay', label: 'Decay', min: 0.01, max: 0.5, default: 0.15, unit: 's' },
  ],
  clap: [
    { name: 'noiseLevel', label: 'Noise', min: 0, max: 1, default: 0.9 },
    { name: 'bodyResAmt', label: 'Body', min: 0, max: 1, default: 0.3 },
    { name: 'transientLevel', label: 'Trans', min: 0, max: 1, default: 0.7 },
    { name: 'filterEnvAmt', label: 'FiltEnv', min: 0, max: 1, default: 0.4 },
    { name: 'earlyReflections', label: 'Room', min: 0, max: 1, default: 0.35 },
    { name: 'decay', label: 'Decay', min: 0.05, max: 1, default: 0.25, unit: 's' },
  ],
  closed_hat: [
    { name: 'oscTune', label: 'Tune', min: 0.5, max: 2, default: 1.0, decimals: 2 },
    { name: 'hiFilterFreq', label: 'HiBand', min: 4000, max: 12000, default: 7100, unit: 'Hz', decimals: 0 },
    { name: 'loFilterFreq', label: 'LoBand', min: 1500, max: 6000, default: 3440, unit: 'Hz', decimals: 0 },
    { name: 'noiseLevel', label: 'Noise', min: 0, max: 1, default: 0.3 },
    { name: 'transientLevel', label: 'Trans', min: 0, max: 1, default: 0.4 },
    { name: 'spectralDecay', label: 'Spectr', min: 0, max: 1, default: 0.5 },
    { name: 'hpfCutoff', label: 'HPF', min: 500, max: 12000, default: 5000, unit: 'Hz', decimals: 0 },
    { name: 'driveAmt', label: 'Drive', min: 0, max: 1, default: 0.15 },
    { name: 'decay', label: 'Decay', min: 0.01, max: 0.2, default: 0.05, unit: 's' },
  ],
  open_hat: [
    { name: 'oscTune', label: 'Tune', min: 0.5, max: 2, default: 1.0, decimals: 2 },
    { name: 'hiFilterFreq', label: 'HiBand', min: 4000, max: 12000, default: 7100, unit: 'Hz', decimals: 0 },
    { name: 'loFilterFreq', label: 'LoBand', min: 1500, max: 6000, default: 3440, unit: 'Hz', decimals: 0 },
    { name: 'noiseLevel', label: 'Noise', min: 0, max: 1, default: 0.3 },
    { name: 'transientLevel', label: 'Trans', min: 0, max: 1, default: 0.4 },
    { name: 'spectralDecay', label: 'Spectr', min: 0, max: 1, default: 0.5 },
    { name: 'hpfCutoff', label: 'HPF', min: 500, max: 12000, default: 4000, unit: 'Hz', decimals: 0 },
    { name: 'driveAmt', label: 'Drive', min: 0, max: 1, default: 0.15 },
    { name: 'decay', label: 'Decay', min: 0.1, max: 2, default: 0.4, unit: 's' },
  ],
  cymbal: [
    { name: 'pitch', label: 'Pitch', min: 200, max: 600, default: 350, unit: 'Hz', decimals: 0 },
    { name: 'size', label: 'Size', min: 0, max: 1, default: 0.5 },
    { name: 'richness', label: 'Rich', min: 0, max: 1, default: 0.6 },
    { name: 'clusterDensity', label: 'Density', min: 0, max: 1, default: 0.5 },
    { name: 'metalCharacter', label: 'Metal', min: 0, max: 1, default: 0.5 },
    { name: 'topAmt', label: 'Top', min: 0, max: 1, default: 0.4 },
    { name: 'driveAmt', label: 'Sat', min: 0, max: 1, default: 0.1 },
    { name: 'toneTilt', label: 'Tilt', min: -1, max: 1, default: 0 },
    { name: 'decay', label: 'Decay', min: 0.2, max: 3, default: 1, unit: 's' },
  ],
  tom: [
    { name: 'pitch', label: 'Pitch', min: 60, max: 300, default: 120, unit: 'Hz', decimals: 0 },
    { name: 'pitchEnvAmt', label: 'Ramp', min: 0, max: 24, default: 6, unit: 'st', decimals: 0 },
    { name: 'pitchRampDecay', label: 'RampD', min: 0.01, max: 0.2, default: 0.05, unit: 's' },
    { name: 'tension', label: 'Tension', min: 0, max: 1, default: 0.5 },
    { name: 'damping', label: 'Damp', min: 0, max: 1, default: 0.3 },
    { name: 'bodyTone', label: 'Body', min: 0, max: 1, default: 0.5 },
    { name: 'overtoneAmt', label: 'Over', min: 0, max: 1, default: 0.4 },
    { name: 'clickLevel', label: 'Click', min: 0, max: 1, default: 0.3 },
    { name: 'decay', label: 'Decay', min: 0.1, max: 1, default: 0.3, unit: 's' },
  ],
  rim: [
    { name: 'pitch', label: 'Pitch', min: 500, max: 2000, default: 1000, unit: 'Hz', decimals: 0 },
    { name: 'dualStrike', label: 'Dual', min: 0, max: 1, default: 0 },
    { name: 'enhance', label: 'Enhance', min: 0, max: 1, default: 0.5 },
    { name: 'feedbackAmt', label: 'Feedbk', min: 0, max: 1, default: 0.3 },
    { name: 'ringFreq', label: 'RingF', min: 500, max: 5000, default: 1500, unit: 'Hz', decimals: 0 },
    { name: 'hpfCutoff', label: 'HPF', min: 100, max: 2000, default: 300, unit: 'Hz', decimals: 0 },
    { name: 'lpfCutoff', label: 'LPF', min: 2000, max: 15000, default: 10000, unit: 'Hz', decimals: 0 },
    { name: 'driveAmt', label: 'Drive', min: 0, max: 1, default: 0.2 },
    { name: 'decay', label: 'Decay', min: 0.02, max: 0.3, default: 0.08, unit: 's' },
  ],
  shaker: [
    { name: 'rattleType', label: 'Type', min: 0, max: 1, default: 0.5 },
    { name: 'grainSize', label: 'Grain', min: 0.001, max: 0.05, default: 0.01, unit: 's' },
    { name: 'grainDensity', label: 'Density', min: 10, max: 200, default: 80, decimals: 0 },
    { name: 'grainRandom', label: 'Random', min: 0, max: 1, default: 0.4 },
    { name: 'hardness', label: 'Hard', min: 0, max: 1, default: 0.5 },
    { name: 'damping', label: 'Damp', min: 0, max: 1, default: 0.4 },
    { name: 'tone', label: 'Tone', min: 0, max: 1, default: 0.5 },
    { name: 'bpCenter', label: 'BPFreq', min: 500, max: 10000, default: 4000, unit: 'Hz', decimals: 0 },
    { name: 'decay', label: 'Decay', min: 0.1, max: 1, default: 0.3, unit: 's' },
  ],
  fold: [
    { name: 'pitch', label: 'Pitch', min: 30, max: 200, default: 60, unit: 'Hz', decimals: 0 },
    { name: 'foldAmt', label: 'Fold', min: 0, max: 1, default: 0.5 },
    { name: 'foldSymmetry', label: 'Sym', min: 0, max: 1, default: 0.5 },
    { name: 'foldBias', label: 'Bias', min: -1, max: 1, default: 0 },
    { name: 'tablePosition', label: 'Table', min: 0, max: 1, default: 0 },
    { name: 'pitchEnvAmt', label: 'PEnv', min: 0, max: 24, default: 0, unit: 'st', decimals: 0 },
    { name: 'cutoff', label: 'Cutoff', min: 100, max: 15000, default: 5000, unit: 'Hz', decimals: 0 },
    { name: 'filterRes', label: 'Reso', min: 0, max: 1, default: 0.3 },
    { name: 'decay', label: 'Decay', min: 0.05, max: 1, default: 0.3, unit: 's' },
  ],
  impact: [
    { name: 'transientType', label: 'Trans', min: 0, max: 4, default: 1, decimals: 0 },
    { name: 'transientBrightness', label: 'TrBrt', min: 0, max: 1, default: 0.5 },
    { name: 'transientLevel', label: 'TrLvl', min: 0, max: 1, default: 0.6 },
    { name: 'bodyType', label: 'Body', min: 0, max: 4, default: 0, decimals: 0 },
    { name: 'bodyPitch', label: 'Pitch', min: 20, max: 2000, default: 200, unit: 'Hz', decimals: 0 },
    { name: 'tailType', label: 'Tail', min: 0, max: 4, default: 0, decimals: 0 },
    { name: 'tailColor', label: 'Color', min: 0, max: 1, default: 0.5 },
    { name: 'layerMix', label: 'Mix', min: 0, max: 1, default: 0.6 },
    { name: 'overallDecay', label: 'Decay', min: 0.05, max: 5, default: 1, unit: 's' },
  ],
  fm: [
    { name: 'pitch', label: 'Pitch', min: 30, max: 200, default: 60, unit: 'Hz', decimals: 0 },
    { name: 'modIndex', label: 'ModIdx', min: 0, max: 20, default: 5, decimals: 1 },
    { name: 'modIndexDecay', label: 'MDecay', min: 0.01, max: 0.5, default: 0.1, unit: 's' },
    { name: 'brightness', label: 'Bright', min: 0, max: 1, default: 0.5 },
    { name: 'inharmonicityBias', label: 'Inharm', min: -1, max: 1, default: 0, decimals: 2 },
    { name: 'algorithm', label: 'Algo', min: 1, max: 8, default: 1, decimals: 0 },
    { name: 'hpfCutoff', label: 'HPF', min: 20, max: 500, default: 20, unit: 'Hz', decimals: 0 },
    { name: 'driveAmt', label: 'Drive', min: 0, max: 1, default: 0.2 },
    { name: 'decay', label: 'Decay', min: 0.05, max: 1, default: 0.3, unit: 's' },
  ],
};

// Display names for engines
export const ENGINE_DISPLAY_NAMES = {
  analog_kick: 'ANALOG KICK',
  membrane: 'MEMBRANE',
  hybrid_snare: 'HYBRID SNARE',
  clap: 'CLAP',
  closed_hat: 'CLOSED HAT',
  open_hat: 'OPEN HAT',
  cymbal: 'CYMBAL',
  tom: 'TOM/CONGA',
  rim: 'RIM/CLAVE',
  shaker: 'SHAKER',
  fold: 'FOLD/WT',
  impact: 'IMPACT',
  fm: 'FM PERC',
};

export const ENGINE_EXPANDED_PARAMS = {
  analog_kick: {
    'Pitch': [
      { name: 'bodyPitch', label: 'Body Pitch', min: 20, max: 200, default: 50, unit: 'Hz', decimals: 0 },
      { name: 'tune', label: 'Tune', min: -24, max: 24, default: 0, unit: 'st', decimals: 0 },
      { name: 'fineTune', label: 'Fine Tune', min: -100, max: 100, default: 0, unit: 'ct', decimals: 0 },
      { name: 'waveShape', label: 'Wave Shape', min: 0, max: 1, default: 0 },
    ],
    'Pitch Envelope': [
      { name: 'pitchEnvAmt', label: 'Amount', min: -48, max: 48, default: 36, unit: 'st', decimals: 0 },
      { name: 'pitchEnvAttack', label: 'Attack', min: 0, max: 0.1, default: 0, unit: 's' },
      { name: 'pitchEnvDecay', label: 'Decay', min: 0.001, max: 0.5, default: 0.08, unit: 's' },
      { name: 'pitchBendAsym', label: 'Asymmetry', min: 0, max: 1, default: 0.7 },
    ],
    'Body': [
      { name: 'bodyDecay', label: 'Decay', min: 0.01, max: 2, default: 0.3, unit: 's' },
      { name: 'bodySustain', label: 'Sustain', min: 0, max: 1, default: 0 },
      { name: 'bodyHold', label: 'Hold', min: 0, max: 0.5, default: 0, unit: 's' },
    ],
    'Click': [
      { name: 'clickLevel', label: 'Level', min: 0, max: 1, default: 0.3 },
      { name: 'clickTone', label: 'Tone', min: 0, max: 1, default: 0.5 },
      { name: 'clickLength', label: 'Length', min: 0.001, max: 0.05, default: 0.005, unit: 's' },
      { name: 'clickSat', label: 'Saturation', min: 0, max: 1, default: 0.2 },
    ],
    'Noise': [
      { name: 'noiseLevel', label: 'Level', min: 0, max: 1, default: 0 },
      { name: 'noiseDecay', label: 'Decay', min: 0.01, max: 1, default: 0.1, unit: 's' },
      { name: 'noiseTransientMix', label: 'Transient Mix', min: 0, max: 1, default: 0.5 },
      { name: 'metalNoiseAmt', label: 'Metal Amount', min: 0, max: 1, default: 0 },
      { name: 'metalNoiseFocus', label: 'Metal Focus', min: 0, max: 1, default: 0.5 },
    ],
    'Harmonics': [
      { name: 'harmonicsAmt', label: 'Amount', min: 0, max: 1, default: 0 },
      { name: 'harmonicsTune', label: 'Tune', min: 0, max: 1, default: 0.5 },
      { name: 'pulseAmt', label: 'Pulse', min: 0, max: 1, default: 0 },
      { name: 'pulseWidth', label: 'Pulse Width', min: 0.1, max: 0.9, default: 0.5 },
      { name: 'pulseDecay', label: 'Pulse Decay', min: 0.01, max: 0.5, default: 0.1, unit: 's' },
    ],
    'Dynamics': [
      { name: 'driveAmt', label: 'Drive', min: 0, max: 1, default: 0.1 },
      { name: 'compAmt', label: 'Compress', min: 0, max: 1, default: 0 },
      { name: 'compAttack', label: 'Comp Attack', min: 0.001, max: 0.1, default: 0.01, unit: 's' },
      { name: 'compRelease', label: 'Comp Release', min: 0.01, max: 0.5, default: 0.1, unit: 's' },
      { name: 'gateThresh', label: 'Gate Thresh', min: 0, max: 1, default: 0 },
      { name: 'gateRelease', label: 'Gate Release', min: 0.01, max: 0.5, default: 0.1, unit: 's' },
    ],
    'EQ': [
      { name: 'lowShelfFreq', label: 'Low Freq', min: 30, max: 200, default: 80, unit: 'Hz', decimals: 0 },
      { name: 'lowShelfGain', label: 'Low Gain', min: -12, max: 12, default: 0, unit: 'dB', decimals: 1 },
      { name: 'midFreq', label: 'Mid Freq', min: 100, max: 2000, default: 500, unit: 'Hz', decimals: 0 },
      { name: 'midGain', label: 'Mid Gain', min: -12, max: 12, default: 0, unit: 'dB', decimals: 1 },
      { name: 'midQ', label: 'Mid Q', min: 0.1, max: 10, default: 1, decimals: 1 },
      { name: 'highShelfFreq', label: 'High Freq', min: 2000, max: 10000, default: 5000, unit: 'Hz', decimals: 0 },
      { name: 'highShelfGain', label: 'High Gain', min: -12, max: 12, default: 0, unit: 'dB', decimals: 1 },
    ],
  },
  membrane: {
    'Membrane 1': [
      { name: 'pitch1', label: 'Pitch', min: 20, max: 500, default: 150, unit: 'Hz', decimals: 0 },
      { name: 'damping1', label: 'Damping', min: 0, max: 1, default: 0.3 },
      { name: 'tension1', label: 'Tension', min: 0, max: 1, default: 0.5 },
      { name: 'nonlinearity1', label: 'Nonlinear', min: 0, max: 1, default: 0.2 },
      { name: 'decayScale1', label: 'Decay Scale', min: 0.1, max: 2, default: 1, decimals: 1 },
    ],
    'Membrane 2': [
      { name: 'pitch2', label: 'Pitch', min: 20, max: 500, default: 200, unit: 'Hz', decimals: 0 },
      { name: 'damping2', label: 'Damping', min: 0, max: 1, default: 0.4 },
      { name: 'tension2', label: 'Tension', min: 0, max: 1, default: 0.6 },
      { name: 'nonlinearity2', label: 'Nonlinear', min: 0, max: 1, default: 0.3 },
      { name: 'decayScale2', label: 'Decay Scale', min: 0.1, max: 2, default: 1, decimals: 1 },
    ],
    'Coupling': [
      { name: 'coupling12', label: 'M1→M2', min: 0, max: 1, default: 0.3 },
      { name: 'coupling21', label: 'M2→M1', min: 0, max: 1, default: 0.2 },
      { name: 'couplingDelay', label: 'Delay', min: 0, max: 0.01, default: 0.001, unit: 's' },
      { name: 'couplingLoss', label: 'Loss', min: 0, max: 1, default: 0.3 },
    ],
    'Strike': [
      { name: 'strikeHardness', label: 'Hardness', min: 0, max: 1, default: 0.6 },
      { name: 'strikePosition', label: 'Position', min: 0, max: 1, default: 0.3 },
      { name: 'strikeLevel', label: 'Level', min: 0, max: 1, default: 0.8 },
      { name: 'strikeNoiseAmt', label: 'Noise', min: 0, max: 1, default: 0.2 },
      { name: 'strikeNoiseColor', label: 'Noise Color', min: 0, max: 1, default: 0.5 },
      { name: 'strikeLength', label: 'Length', min: 0.001, max: 0.05, default: 0.01, unit: 's' },
    ],
    'Snare Wire': [
      { name: 'wireLevel', label: 'Amount', min: 0, max: 1, default: 0 },
      { name: 'snareWireTension', label: 'Tension', min: 0, max: 1, default: 0.5 },
      { name: 'snareWireDecay', label: 'Decay', min: 0.01, max: 1, default: 0.2, unit: 's' },
      { name: 'snareWireTone', label: 'Tone', min: 0, max: 1, default: 0.5 },
    ],
    'Shell': [
      { name: 'shellSize', label: 'Size', min: 0, max: 1, default: 0.5 },
      { name: 'shellResAmt', label: 'Resonance', min: 0, max: 1, default: 0.3 },
      { name: 'shellResFreq', label: 'Res Freq', min: 50, max: 500, default: 200, unit: 'Hz', decimals: 0 },
      { name: 'shellDamping', label: 'Damping', min: 0, max: 1, default: 0.5 },
      { name: 'airPortAmt', label: 'Air Port', min: 0, max: 1, default: 0.3 },
    ],
    'Tone': [
      { name: 'noiseFilterCutoff', label: 'Noise Filter', min: 100, max: 10000, default: 5000, unit: 'Hz', decimals: 0 },
      { name: 'noiseFilterRes', label: 'Filter Res', min: 0, max: 1, default: 0.3 },
      { name: 'toneTilt', label: 'Tilt', min: -1, max: 1, default: 0 },
      { name: 'wavefoldAmt', label: 'Wavefold', min: 0, max: 1, default: 0 },
      { name: 'driveAmt', label: 'Distortion', min: 0, max: 1, default: 0 },
      { name: 'decay', label: 'Decay', min: 0.05, max: 1, default: 0.3, unit: 's' },
    ],
  },
  hybrid_snare: {
    'Body': [
      { name: 'bodyPitch', label: 'Pitch', min: 80, max: 400, default: 180, unit: 'Hz', decimals: 0 },
      { name: 'bodyDecay', label: 'Decay', min: 0.01, max: 0.5, default: 0.15, unit: 's' },
      { name: 'bodyTone', label: 'Tone', min: 0, max: 1, default: 0.5 },
      { name: 'bodyDetune', label: 'Detune', min: 0, max: 50, default: 0, unit: 'ct', decimals: 0 },
    ],
    'Bump': [
      { name: 'bumpAmt', label: 'Amount', min: 0, max: 1, default: 0.4 },
      { name: 'bumpPitch', label: 'Pitch', min: 50, max: 300, default: 120, unit: 'Hz', decimals: 0 },
      { name: 'bumpAttack', label: 'Attack', min: 0.001, max: 0.02, default: 0.002, unit: 's' },
      { name: 'bumpDecay', label: 'Decay', min: 0.01, max: 0.1, default: 0.04, unit: 's' },
      { name: 'bumpCurve', label: 'Curve', min: 0, max: 1, default: 0.5 },
    ],
    'Snare Wire': [
      { name: 'wireLevel', label: 'Amount', min: 0, max: 1, default: 0.4 },
      { name: 'wireTension', label: 'Tension', min: 0, max: 1, default: 0.5 },
      { name: 'wireDecay', label: 'Decay', min: 0.05, max: 0.5, default: 0.2, unit: 's' },
    ],
    'Noise': [
      { name: 'noiseLevel', label: 'Amount', min: 0, max: 1, default: 0.6 },
      { name: 'noiseDecay', label: 'Decay', min: 0.05, max: 0.5, default: 0.15, unit: 's' },
      { name: 'noiseColor', label: 'Color', min: 0, max: 1, default: 0.5 },
      { name: 'noiseBandCutoff', label: 'Band Cutoff', min: 500, max: 10000, default: 3000, unit: 'Hz', decimals: 0 },
      { name: 'noiseBandQ', label: 'Band Q', min: 0.1, max: 10, default: 1, decimals: 1 },
      { name: 'noiseSnap', label: 'Snap', min: 0, max: 1, default: 0.5 },
      { name: 'noiseNarrowAmt', label: 'Narrow', min: 0, max: 1, default: 0.5 },
      { name: 'noiseWideAmt', label: 'Wide', min: 0, max: 1, default: 0.3 },
    ],
    'Filter Envelope': [
      { name: 'filterEnvAmt', label: 'Amount', min: 0, max: 1, default: 0.3 },
      { name: 'filterEnvDecay', label: 'Decay', min: 0.01, max: 0.2, default: 0.08, unit: 's' },
    ],
    'Coupling': [
      { name: 'couplingAmt', label: 'Amount', min: 0, max: 1, default: 0.2 },
    ],
    'Ring': [
      { name: 'ringAmt', label: 'Amount', min: 0, max: 1, default: 0.2 },
      { name: 'ringFreq', label: 'Freq', min: 100, max: 2000, default: 400, unit: 'Hz', decimals: 0 },
      { name: 'ringDecay', label: 'Decay', min: 0.01, max: 0.3, default: 0.1, unit: 's' },
      { name: 'ringSpread', label: 'Spread', min: 0, max: 1, default: 0.3 },
    ],
    'FM': [
      { name: 'fmAmt', label: 'Amount', min: 0, max: 1, default: 0 },
      { name: 'fmFreq', label: 'Freq', min: 50, max: 2000, default: 500, unit: 'Hz', decimals: 0 },
      { name: 'fmDecay', label: 'Decay', min: 0.01, max: 0.2, default: 0.05, unit: 's' },
      { name: 'fmFeedback', label: 'Feedback', min: 0, max: 1, default: 0 },
    ],
    'Filter': [
      { name: 'hpfCutoff', label: 'HPF', min: 20, max: 500, default: 80, unit: 'Hz', decimals: 0 },
      { name: 'lpfCutoff', label: 'LPF', min: 2000, max: 20000, default: 12000, unit: 'Hz', decimals: 0 },
      { name: 'filterRes', label: 'Resonance', min: 0, max: 1, default: 0 },
      { name: 'eqMidFreq', label: 'Mid Freq', min: 500, max: 5000, default: 2000, unit: 'Hz', decimals: 0 },
      { name: 'eqMidGain', label: 'Mid Gain', min: -12, max: 12, default: 0, unit: 'dB', decimals: 1 },
      { name: 'eqMidQ', label: 'Mid Q', min: 0.1, max: 10, default: 1, decimals: 1 },
    ],
    'Output': [
      { name: 'driveAmt', label: 'Clip', min: 0, max: 1, default: 0.2 },
      { name: 'postClipTone', label: 'Post Tone', min: -1, max: 1, default: 0 },
    ],
  },
  clap: {
    'Envelope': [
      { name: 'decay', label: 'Decay', min: 0.05, max: 1, default: 0.25, unit: 's' },
      { name: 'attack', label: 'Attack', min: 0.001, max: 0.02, default: 0.002, unit: 's' },
      { name: 'noiseWidth', label: 'Noise Width', min: 0.01, max: 0.2, default: 0.06, unit: 's' },
    ],
    'Noise': [
      { name: 'noiseLevel', label: 'Amount', min: 0, max: 1, default: 0.9 },
      { name: 'noiseColor', label: 'Color', min: 0, max: 1, default: 0.6 },
      { name: 'noiseResonance', label: 'Resonance', min: 0, max: 1, default: 0.4 },
    ],
    'Body Resonance': [
      { name: 'bodyResAmt', label: 'Amount', min: 0, max: 1, default: 0.3 },
      { name: 'bodyResFreq', label: 'Freq', min: 150, max: 400, default: 250, unit: 'Hz', decimals: 0 },
      { name: 'bodyResDecay', label: 'Decay', min: 0.05, max: 0.3, default: 0.12, unit: 's' },
    ],
    'Filter Envelope': [
      { name: 'filterEnvAmt', label: 'Amount', min: 0, max: 1, default: 0.4 },
      { name: 'filterEnvDecay', label: 'Decay', min: 0.01, max: 0.1, default: 0.04, unit: 's' },
    ],
    'Tone': [
      { name: 'toneAmt', label: 'Amount', min: 0, max: 1, default: 0.35 },
      { name: 'tonePitch', label: 'Pitch', min: 100, max: 1000, default: 280, unit: 'Hz', decimals: 0 },
      { name: 'toneDecay', label: 'Decay', min: 0.01, max: 0.2, default: 0.05, unit: 's' },
    ],
    'Transient': [
      { name: 'transientLevel', label: 'Level', min: 0, max: 1, default: 0.7 },
      { name: 'transientLength', label: 'Length', min: 0.001, max: 0.02, default: 0.005, unit: 's' },
      { name: 'transientFreq', label: 'Freq', min: 1000, max: 8000, default: 3500, unit: 'Hz', decimals: 0 },
    ],
    'Room': [
      { name: 'earlyReflections', label: 'Reflections', min: 0, max: 1, default: 0.35 },
      { name: 'roomSize', label: 'Size', min: 0, max: 1, default: 0.35 },
    ],
    'Filter': [
      { name: 'hpfCutoff', label: 'HPF', min: 100, max: 2000, default: 350, unit: 'Hz', decimals: 0 },
      { name: 'lpfCutoff', label: 'LPF', min: 2000, max: 15000, default: 10000, unit: 'Hz', decimals: 0 },
      { name: 'bandEmphasisFreq', label: 'Emphasis Freq', min: 500, max: 5000, default: 2200, unit: 'Hz', decimals: 0 },
      { name: 'bandEmphasisQ', label: 'Emphasis Q', min: 0.5, max: 5, default: 1.4, decimals: 1 },
      { name: 'highEmphasis', label: 'High Emphasis', min: 0, max: 1, default: 0.55 },
    ],
    'Strike': [
      { name: 'strikeType', label: 'Type', min: 0, max: 2, default: 0, decimals: 0, options: ['palm', 'fingers', 'cupped'] },
    ],
    'Drive': [
      { name: 'driveAmt', label: 'Drive', min: 0, max: 1, default: 0.2 },
      { name: 'driveType', label: 'Type', min: 0, max: 2, default: 0, decimals: 0, options: ['soft', 'hard', 'tape'] },
      { name: 'driveHardness', label: 'Hardness', min: 0, max: 1, default: 0.5 },
      { name: 'postDriveTone', label: 'Post Tone', min: -1, max: 1, default: 0.15 },
    ],
  },
  closed_hat: {
    'Envelope': [
      { name: 'decay', label: 'Decay', min: 0.01, max: 0.2, default: 0.05, unit: 's' },
      { name: 'attack', label: 'Attack', min: 0, max: 0.05, default: 0.001, unit: 's' },
      { name: 'hold', label: 'Hold', min: 0, max: 0.5, default: 0, unit: 's' },
      { name: 'spectralDecay', label: 'Spectral', min: 0, max: 1, default: 0.5 },
    ],
    'Oscillators': [
      { name: 'oscTune', label: 'Master Tune', min: 0.5, max: 2, default: 1.0, decimals: 2 },
      { name: 'osc1Freq', label: 'Osc 1', min: 200, max: 1200, default: 800, unit: 'Hz', decimals: 0 },
      { name: 'osc2Freq', label: 'Osc 2', min: 200, max: 800, default: 540, unit: 'Hz', decimals: 0 },
      { name: 'pulseWidth', label: 'Pulse W', min: 0.1, max: 0.9, default: 0.5 },
    ],
    'Modulation': [
      { name: 'modDepth', label: 'Depth', min: 0, max: 1, default: 0.3 },
      { name: 'modRatio', label: 'Ratio', min: 0.25, max: 4, default: 1.4, decimals: 2 },
      { name: 'modDecay', label: 'Decay', min: 0.01, max: 0.5, default: 0.08, unit: 's' },
    ],
    'Dual Bandpass': [
      { name: 'hiFilterFreq', label: 'Hi Freq', min: 4000, max: 12000, default: 7100, unit: 'Hz', decimals: 0 },
      { name: 'hiFilterQ', label: 'Hi Q', min: 0.5, max: 10, default: 2, decimals: 1 },
      { name: 'hiFilterDecay', label: 'Hi Decay', min: 0.01, max: 2, default: 0.05, unit: 's' },
      { name: 'hiFilterLevel', label: 'Hi Level', min: 0, max: 1, default: 0.7 },
      { name: 'loFilterFreq', label: 'Lo Freq', min: 1500, max: 6000, default: 3440, unit: 'Hz', decimals: 0 },
      { name: 'loFilterQ', label: 'Lo Q', min: 0.5, max: 10, default: 2, decimals: 1 },
      { name: 'loFilterDecay', label: 'Lo Decay', min: 0.01, max: 2, default: 0.08, unit: 's' },
      { name: 'loFilterLevel', label: 'Lo Level', min: 0, max: 1, default: 0.5 },
      { name: 'bandBalance', label: 'Balance', min: -1, max: 1, default: 0.3 },
    ],
    'Noise': [
      { name: 'noiseLevel', label: 'Level', min: 0, max: 1, default: 0.3 },
      { name: 'noiseFilterFreq', label: 'Filter', min: 2000, max: 15000, default: 8000, unit: 'Hz', decimals: 0 },
      { name: 'noiseFilterQ', label: 'Q', min: 0.5, max: 5, default: 1, decimals: 1 },
      { name: 'noiseDecay', label: 'Decay', min: 0.01, max: 2, default: 0.05, unit: 's' },
    ],
    'Transient': [
      { name: 'transientLevel', label: 'Amount', min: 0, max: 1, default: 0.4 },
      { name: 'transientFreq', label: 'Freq', min: 3000, max: 12000, default: 8000, unit: 'Hz', decimals: 0 },
      { name: 'transientLength', label: 'Length', min: 0.001, max: 0.03, default: 0.005, unit: 's' },
    ],
    'Peak': [
      { name: 'peakFreq', label: 'Freq', min: 2000, max: 15000, default: 8000, unit: 'Hz', decimals: 0 },
      { name: 'peakAmt', label: 'Amount', min: 0, max: 1, default: 0.2 },
      { name: 'peakQ', label: 'Q', min: 1, max: 30, default: 8, decimals: 0 },
    ],
    'Post Filter': [
      { name: 'hpfCutoff', label: 'HPF', min: 500, max: 12000, default: 5000, unit: 'Hz', decimals: 0 },
      { name: 'hpfRes', label: 'HPF Res', min: 0, max: 1, default: 0.3 },
      { name: 'lpfCutoff', label: 'LPF', min: 3000, max: 20000, default: 15000, unit: 'Hz', decimals: 0 },
      { name: 'lpfRes', label: 'LPF Res', min: 0, max: 1, default: 0.2 },
    ],
    'Character': [
      { name: 'sizzleRate', label: 'Sizzle Rate', min: 10, max: 200, default: 40, unit: 'Hz', decimals: 0 },
      { name: 'sizzleDepth', label: 'Sizzle Depth', min: 0, max: 1, default: 0.15 },
      { name: 'gapAmt', label: 'Gap', min: 0, max: 1, default: 0 },
      { name: 'gapRate', label: 'Gap Rate', min: 0, max: 1, default: 0.5 },
      { name: 'driveAmt', label: 'Drive', min: 0, max: 1, default: 0.15 },
      { name: 'bitDepth', label: 'Bit Depth', min: 4, max: 16, default: 16, decimals: 0 },
      { name: 'sampleRateReduce', label: 'SRR', min: 0, max: 1, default: 0 },
    ],
  },
  open_hat: {
    'Envelope': [
      { name: 'decay', label: 'Decay', min: 0.1, max: 3, default: 0.4, unit: 's' },
      { name: 'attack', label: 'Attack', min: 0, max: 0.05, default: 0.001, unit: 's' },
      { name: 'hold', label: 'Hold', min: 0, max: 0.5, default: 0, unit: 's' },
      { name: 'spectralDecay', label: 'Spectral', min: 0, max: 1, default: 0.5 },
    ],
    'Oscillators': [
      { name: 'oscTune', label: 'Master Tune', min: 0.5, max: 2, default: 1.0, decimals: 2 },
      { name: 'osc1Freq', label: 'Osc 1', min: 200, max: 1200, default: 800, unit: 'Hz', decimals: 0 },
      { name: 'osc2Freq', label: 'Osc 2', min: 200, max: 800, default: 540, unit: 'Hz', decimals: 0 },
      { name: 'pulseWidth', label: 'Pulse W', min: 0.1, max: 0.9, default: 0.5 },
    ],
    'Modulation': [
      { name: 'modDepth', label: 'Depth', min: 0, max: 1, default: 0.3 },
      { name: 'modRatio', label: 'Ratio', min: 0.25, max: 4, default: 1.4, decimals: 2 },
      { name: 'modDecay', label: 'Decay', min: 0.01, max: 0.5, default: 0.08, unit: 's' },
    ],
    'Dual Bandpass': [
      { name: 'hiFilterFreq', label: 'Hi Freq', min: 4000, max: 12000, default: 7100, unit: 'Hz', decimals: 0 },
      { name: 'hiFilterQ', label: 'Hi Q', min: 0.5, max: 10, default: 2, decimals: 1 },
      { name: 'hiFilterDecay', label: 'Hi Decay', min: 0.01, max: 2, default: 0.3, unit: 's' },
      { name: 'hiFilterLevel', label: 'Hi Level', min: 0, max: 1, default: 0.7 },
      { name: 'loFilterFreq', label: 'Lo Freq', min: 1500, max: 6000, default: 3440, unit: 'Hz', decimals: 0 },
      { name: 'loFilterQ', label: 'Lo Q', min: 0.5, max: 10, default: 2, decimals: 1 },
      { name: 'loFilterDecay', label: 'Lo Decay', min: 0.01, max: 2, default: 0.35, unit: 's' },
      { name: 'loFilterLevel', label: 'Lo Level', min: 0, max: 1, default: 0.5 },
      { name: 'bandBalance', label: 'Balance', min: -1, max: 1, default: 0.3 },
    ],
    'Noise': [
      { name: 'noiseLevel', label: 'Level', min: 0, max: 1, default: 0.3 },
      { name: 'noiseFilterFreq', label: 'Filter', min: 2000, max: 15000, default: 8000, unit: 'Hz', decimals: 0 },
      { name: 'noiseFilterQ', label: 'Q', min: 0.5, max: 5, default: 1, decimals: 1 },
      { name: 'noiseDecay', label: 'Decay', min: 0.01, max: 2, default: 0.3, unit: 's' },
    ],
    'Transient': [
      { name: 'transientLevel', label: 'Amount', min: 0, max: 1, default: 0.4 },
      { name: 'transientFreq', label: 'Freq', min: 3000, max: 12000, default: 8000, unit: 'Hz', decimals: 0 },
      { name: 'transientLength', label: 'Length', min: 0.001, max: 0.03, default: 0.005, unit: 's' },
    ],
    'Peak': [
      { name: 'peakFreq', label: 'Freq', min: 2000, max: 15000, default: 8000, unit: 'Hz', decimals: 0 },
      { name: 'peakAmt', label: 'Amount', min: 0, max: 1, default: 0.2 },
      { name: 'peakQ', label: 'Q', min: 1, max: 30, default: 8, decimals: 0 },
    ],
    'Post Filter': [
      { name: 'hpfCutoff', label: 'HPF', min: 500, max: 12000, default: 4000, unit: 'Hz', decimals: 0 },
      { name: 'hpfRes', label: 'HPF Res', min: 0, max: 1, default: 0.3 },
      { name: 'lpfCutoff', label: 'LPF', min: 3000, max: 20000, default: 15000, unit: 'Hz', decimals: 0 },
      { name: 'lpfRes', label: 'LPF Res', min: 0, max: 1, default: 0.2 },
    ],
    'Character': [
      { name: 'sizzleRate', label: 'Sizzle Rate', min: 10, max: 200, default: 40, unit: 'Hz', decimals: 0 },
      { name: 'sizzleDepth', label: 'Sizzle Depth', min: 0, max: 1, default: 0.15 },
      { name: 'gapAmt', label: 'Gap', min: 0, max: 1, default: 0 },
      { name: 'gapRate', label: 'Gap Rate', min: 0, max: 1, default: 0.5 },
      { name: 'driveAmt', label: 'Drive', min: 0, max: 1, default: 0.15 },
      { name: 'bitDepth', label: 'Bit Depth', min: 4, max: 16, default: 16, decimals: 0 },
      { name: 'sampleRateReduce', label: 'SRR', min: 0, max: 1, default: 0 },
    ],
  },
  cymbal: {
    'Core': [
      { name: 'size', label: 'Size', min: 0, max: 1, default: 0.5 },
      { name: 'decay', label: 'Decay', min: 0.5, max: 10, default: 3, unit: 's' },
      { name: 'richness', label: 'Richness', min: 0, max: 1, default: 0.6 },
    ],
    'Top': [
      { name: 'topAmt', label: 'Amount', min: 0, max: 1, default: 0.5 },
      { name: 'topTuning', label: 'Tuning', min: 0, max: 1, default: 0.5 },
      { name: 'edgePeakAmt', label: 'Edge Peak', min: 0, max: 1, default: 0.3 },
    ],
    'Inharmonic': [
      { name: 'clusterDensity', label: 'Density', min: 0, max: 1, default: 0.5 },
      { name: 'inharmonicity', label: 'Inharmonic', min: 0, max: 1, default: 0.4 },
      { name: 'feedbackAmt', label: 'Feedback', min: 0, max: 1, default: 0.2 },
      { name: 'metalCharacter', label: 'Metal Char', min: 0, max: 1, default: 0.3 },
    ],
    'Filter': [
      { name: 'hpfCutoff', label: 'HPF', min: 500, max: 5000, default: 1000, unit: 'Hz', decimals: 0 },
      { name: 'lpfCutoff', label: 'LPF', min: 5000, max: 20000, default: 18000, unit: 'Hz', decimals: 0 },
      { name: 'filterRes', label: 'Resonance', min: 0, max: 1, default: 0.1 },
      { name: 'toneTilt', label: 'Tilt EQ', min: -1, max: 1, default: 0 },
    ],
    'Saturation': [
      { name: 'driveAmt', label: 'Amount', min: 0, max: 1, default: 0.1 },
    ],
  },
  tom: {
    'Pitch': [
      { name: 'pitch', label: 'Pitch', min: 40, max: 400, default: 120, unit: 'Hz', decimals: 0 },
      { name: 'decay', label: 'Decay', min: 0.05, max: 2, default: 0.3, unit: 's' },
      { name: 'pitchEnvAmt', label: 'Ramp Amt', min: -24, max: 24, default: 12, unit: 'st', decimals: 0 },
      { name: 'pitchRampDecay', label: 'Ramp Decay', min: 0.01, max: 0.3, default: 0.08, unit: 's' },
      { name: 'damping', label: 'Damping', min: 0, max: 1, default: 0.3 },
    ],
    'Strike': [
      { name: 'clickLevel', label: 'Click', min: 0, max: 1, default: 0.3 },
      { name: 'clickHardness', label: 'Hardness', min: 0, max: 1, default: 0.5 },
      { name: 'attackLength', label: 'Attack Len', min: 0.001, max: 0.02, default: 0.005, unit: 's' },
    ],
    'Tone': [
      { name: 'bodyTone', label: 'Body Tone', min: 0, max: 1, default: 0.5 },
      { name: 'overtoneAmt', label: 'Overtones', min: 0, max: 1, default: 0.3 },
      { name: 'overtoneTuning', label: 'Over Tune', min: 0, max: 1, default: 0.5 },
      { name: 'driveAmt', label: 'Distortion', min: 0, max: 1, default: 0 },
      { name: 'distortionHardness', label: 'Dist Hard', min: 0, max: 1, default: 0.5 },
    ],
    'Skin': [
      { name: 'tension', label: 'Tension', min: 0, max: 1, default: 0.5 },
      { name: 'hitIntensityCurve', label: 'Hit Curve', min: 0, max: 1, default: 0.5 },
    ],
  },
  rim: {
    'Pitch': [
      { name: 'pitch', label: 'Pitch', min: 200, max: 2000, default: 800, unit: 'Hz', decimals: 0 },
      { name: 'decay', label: 'Decay', min: 0.01, max: 0.5, default: 0.08, unit: 's' },
      { name: 'dualStrike', label: 'Dual Strike', min: 0, max: 1, default: 0 },
      { name: 'enhance', label: 'Enhance', min: 0, max: 1, default: 0.5 },
      { name: 'detune', label: 'Detune', min: 0, max: 50, default: 0, unit: 'ct', decimals: 0 },
    ],
    'Snare': [
      { name: 'snareTimbreAmt', label: 'Snare Amt', min: 0, max: 1, default: 0 },
      { name: 'snarePitch', label: 'Snare Pitch', min: 100, max: 500, default: 200, unit: 'Hz', decimals: 0 },
      { name: 'snareDecay', label: 'Snare Decay', min: 0.01, max: 0.2, default: 0.05, unit: 's' },
    ],
    'Ring': [
      { name: 'feedbackAmt', label: 'Feedback', min: 0, max: 1, default: 0.2 },
      { name: 'ringFreq', label: 'Freq', min: 500, max: 5000, default: 1500, unit: 'Hz', decimals: 0 },
      { name: 'ringDecay', label: 'Decay', min: 0.01, max: 0.3, default: 0.05, unit: 's' },
    ],
    'Filter': [
      { name: 'hpfCutoff', label: 'HPF', min: 100, max: 2000, default: 300, unit: 'Hz', decimals: 0 },
      { name: 'lpfCutoff', label: 'LPF', min: 2000, max: 15000, default: 10000, unit: 'Hz', decimals: 0 },
      { name: 'filterRes', label: 'Resonance', min: 0, max: 1, default: 0.2 },
    ],
    'Drive': [
      { name: 'driveAmt', label: 'Clip', min: 0, max: 1, default: 0.1 },
    ],
  },
  shaker: {
    'Grain': [
      { name: 'rattleAmt', label: 'Rattle', min: 0, max: 1, default: 0.7 },
      { name: 'grainSize', label: 'Size', min: 0, max: 1, default: 0.3 },
      { name: 'grainDensity', label: 'Density', min: 0, max: 1, default: 0.6 },
      { name: 'grainRandom', label: 'Random', min: 0, max: 1, default: 0.4 },
    ],
    'Motion': [
      { name: 'attackLength', label: 'Attack', min: 0.01, max: 0.2, default: 0.05, unit: 's' },
      { name: 'sustainLength', label: 'Sustain', min: 0.05, max: 1, default: 0.2, unit: 's' },
      { name: 'damping', label: 'Damping', min: 0, max: 1, default: 0.4 },
      { name: 'hardness', label: 'Hardness', min: 0, max: 1, default: 0.5 },
    ],
    'Tone': [
      { name: 'tone', label: 'Tone', min: 0, max: 1, default: 0.5 },
      { name: 'bpCenter', label: 'BP Center', min: 500, max: 10000, default: 4000, unit: 'Hz', decimals: 0 },
      { name: 'bpQ', label: 'BP Q', min: 0.5, max: 5, default: 1, decimals: 1 },
      { name: 'noiseColor', label: 'Noise Color', min: 0, max: 1, default: 0.5 },
    ],
  },
  fold: {
    'Pitch': [
      { name: 'tune', label: 'Tune', min: -24, max: 24, default: 0, unit: 'st', decimals: 0 },
      { name: 'fineTune', label: 'Fine', min: -100, max: 100, default: 0, unit: 'ct', decimals: 0 },
      { name: 'decay', label: 'Decay', min: 0.01, max: 2, default: 0.3, unit: 's' },
      { name: 'pitchEnvAmt', label: 'Pitch Env', min: -48, max: 48, default: 24, unit: 'st', decimals: 0 },
      { name: 'pitchEnvDecay', label: 'Env Decay', min: 0.001, max: 0.5, default: 0.1, unit: 's' },
    ],
    'Fold': [
      { name: 'foldAmt', label: 'Amount', min: 0, max: 1, default: 0.5 },
      { name: 'foldSymmetry', label: 'Symmetry', min: 0, max: 1, default: 0.5 },
      { name: 'foldBias', label: 'Bias', min: -1, max: 1, default: 0 },
      { name: 'foldPreGain', label: 'Pre Gain', min: 0, max: 1, default: 0.5 },
      { name: 'foldPostTone', label: 'Post Tone', min: 0, max: 1, default: 0.5 },
    ],
    'Wavetable': [
      { name: 'waveSelect', label: 'Wave', min: 0, max: 127, default: 0, decimals: 0 },
      { name: 'tablePosition', label: 'Position', min: 0, max: 1, default: 0 },
      { name: 'tableWarp', label: 'Warp', min: 0, max: 1, default: 0 },
      { name: 'tableSmooth', label: 'Smooth', min: 0, max: 1, default: 0.5 },
      { name: 'harmonicEmphasis', label: 'Harmonic', min: 0, max: 1, default: 0.5 },
    ],
    'Complex': [
      { name: 'modOscFreq', label: 'Mod Freq', min: 0.1, max: 10, default: 1, decimals: 1 },
      { name: 'modAmt', label: 'Mod Amt', min: 0, max: 1, default: 0 },
      { name: 'noiseInjection', label: 'Noise', min: 0, max: 1, default: 0 },
      { name: 'partialSpread', label: 'Spread', min: 0, max: 1, default: 0 },
    ],
    'Filter': [
      { name: 'cutoff', label: 'Cutoff', min: 100, max: 15000, default: 5000, unit: 'Hz', decimals: 0 },
      { name: 'filterRes', label: 'Resonance', min: 0, max: 1, default: 0.2 },
      { name: 'driveAmt', label: 'Drive', min: 0, max: 1, default: 0.2 },
      { name: 'driveMix', label: 'Drive Mix', min: 0, max: 1, default: 0.5 },
    ],
  },
  impact: {
    'Transient': [
      { name: 'transientType', label: 'Type', min: 0, max: 4, default: 1, decimals: 0 },
      { name: 'transientPitch', label: 'Pitch', min: 100, max: 8000, default: 1000, unit: 'Hz', decimals: 0 },
      { name: 'transientBrightness', label: 'Brightness', min: 0, max: 1, default: 0.5 },
      { name: 'transientLevel', label: 'Level', min: 0, max: 1, default: 0.6 },
      { name: 'transientLength', label: 'Length', min: 0.001, max: 0.1, default: 0.02, unit: 's' },
    ],
    'Body': [
      { name: 'bodyType', label: 'Type', min: 0, max: 4, default: 0, decimals: 0 },
      { name: 'bodyPitch', label: 'Pitch', min: 20, max: 2000, default: 200, unit: 'Hz', decimals: 0 },
      { name: 'bodyDecay', label: 'Decay', min: 0.01, max: 2, default: 0.3, unit: 's' },
      { name: 'bodyFilterCutoff', label: 'Filter', min: 100, max: 15000, default: 5000, unit: 'Hz', decimals: 0 },
      { name: 'bodyFilterRes', label: 'Reso', min: 0, max: 1, default: 0.2 },
      { name: 'bodyLevel', label: 'Level', min: 0, max: 1, default: 0.7 },
    ],
    'Tail': [
      { name: 'tailType', label: 'Type', min: 0, max: 3, default: 0, decimals: 0 },
      { name: 'tailDecay', label: 'Decay', min: 0.1, max: 5, default: 0.8, unit: 's' },
      { name: 'tailColor', label: 'Color', min: 0, max: 1, default: 0.5 },
      { name: 'tailLevel', label: 'Level', min: 0, max: 1, default: 0.5 },
    ],
    'Global': [
      { name: 'layerMix', label: 'Layer Mix', min: 0, max: 1, default: 0.5 },
      { name: 'overallDecay', label: 'Overall Decay', min: 0.05, max: 5, default: 1, unit: 's' },
      { name: 'hpfCutoff', label: 'HPF', min: 20, max: 1000, default: 20, unit: 'Hz', decimals: 0 },
      { name: 'lpfCutoff', label: 'LPF', min: 1000, max: 20000, default: 15000, unit: 'Hz', decimals: 0 },
      { name: 'driveAmt', label: 'Drive', min: 0, max: 1, default: 0 },
    ],
  },
  fm: {
    'Pitch': [
      { name: 'pitch', label: 'Pitch', min: 20, max: 500, default: 60, unit: 'Hz', decimals: 0 },
      { name: 'algorithm', label: 'Algorithm', min: 1, max: 8, default: 1, decimals: 0 },
    ],
    'Modulation': [
      { name: 'modIndex', label: 'Index', min: 0, max: 10, default: 2, decimals: 1 },
      { name: 'modIndexDecay', label: 'Index Decay', min: 0.01, max: 0.5, default: 0.1, unit: 's' },
      { name: 'brightness', label: 'Brightness', min: 0, max: 1, default: 0.5 },
      { name: 'inharmonicityBias', label: 'Inharm Bias', min: -1, max: 1, default: 0 },
    ],
    'Filter': [
      { name: 'hpfCutoff', label: 'HPF', min: 20, max: 500, default: 20, unit: 'Hz', decimals: 0 },
      { name: 'lpfCutoff', label: 'LPF', min: 500, max: 20000, default: 15000, unit: 'Hz', decimals: 0 },
      { name: 'filterRes', label: 'Resonance', min: 0, max: 1, default: 0 },
    ],
    'Drive': [
      { name: 'driveAmt', label: 'Drive', min: 0, max: 1, default: 0.1 },
    ],
  },
};

// Short names — what the matrix prints in each row's identity column.
export const ENGINE_SHORT_NAMES = {
  analog_kick: 'KICK',
  membrane: 'MEMB',
  hybrid_snare: 'SNARE',
  clap: 'CLAP',
  closed_hat: 'CH',
  open_hat: 'OH',
  cymbal: 'CYMB',
  tom: 'TOM',
  rim: 'RIM',
  shaker: 'SHAKE',
  fold: 'FOLD',
  impact: 'IMP',
  fm: 'FM',
};
